import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import MobileChatService from '../../services/MobileChatService';
import EnhancedMessageRenderer from '../EnhancedMessageRenderer';
import { useTheme, THEMES } from '../../services/themeContext';
import '../../styles/enhancedRenderer.css';

/**
 * Self-contained mobile chat page.
 * Bypasses MSAL — SAP SSO cookies are forwarded via credentials: 'include'.
 * Agent configuration is fetched from the backend on mount.
 *
 * Supports light and dark themes via the shared ThemeProvider.
 */

/** Color palettes for light and dark themes. */
const darkColors = {
  containerBg: '#0f172a',
  textPrimary: '#e2e8f0',
  textSecondary: '#94a3b8',
  headerBg: '#1e293b',
  headerBorder: '#1e293b',
  userMessageBg: '#3b82f6',
  userMessageText: '#ffffff',
  assistantMessageBg: '#1e293b',
  assistantMessageText: '#e2e8f0',
  errorMessageBg: '#7f1d1d',
  errorMessageText: '#fecaca',
  inputBorder: '#334155',
  inputBg: '#0f172a',
  inputText: '#e2e8f0',
  sendButtonBg: '#3b82f6',
  sendButtonText: '#ffffff',
  dotBg: '#94a3b8',
  toggleBg: 'rgba(255, 255, 255, 0.1)',
  toggleText: '#e2e8f0',
};

const lightColors = {
  containerBg: '#f8fafc',
  textPrimary: '#1e293b',
  textSecondary: '#64748b',
  headerBg: '#ffffff',
  headerBorder: '#e2e8f0',
  userMessageBg: '#3b82f6',
  userMessageText: '#ffffff',
  assistantMessageBg: '#ffffff',
  assistantMessageText: '#1e293b',
  errorMessageBg: '#fef2f2',
  errorMessageText: '#991b1b',
  inputBorder: '#cbd5e1',
  inputBg: '#ffffff',
  inputText: '#1e293b',
  sendButtonBg: '#3b82f6',
  sendButtonText: '#ffffff',
  dotBg: '#94a3b8',
  toggleBg: 'rgba(0, 0, 0, 0.06)',
  toggleText: '#475569',
};

function getInitialMessageFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const contextMessage = (params.get('ctx-msg') || '').trim();
  const contextDescription = (params.get('ctx-desc') || '').trim();
  const contextWarehouse = (params.get('ctx-whs') || '').trim();

  if (contextMessage || contextDescription || contextWarehouse) {
    const lines = ['SAP context:'];

    if (contextMessage) {
      lines.push(`Message: ${contextMessage}`);
    }
    if (contextDescription) {
      lines.push(`Description: ${contextDescription}`);
    }
    if (contextWarehouse) {
      lines.push(`Warehouse: ${contextWarehouse}`);
    }

    return lines.join('\n');
  }

  return (params.get('initialMessage') || params.get('prompt') || '').trim();
}

function MobileChatPage() {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [agentConfig, setAgentConfig] = useState(null);
  const [configError, setConfigError] = useState(null);
  const [previousResponseId, setPreviousResponseId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const initialMessageRef = useRef(getInitialMessageFromUrl());
  const hasInjectedInitialMessageRef = useRef(false);
  const { theme, toggleTheme } = useTheme();
  const colors = theme === THEMES.DARK ? darkColors : lightColors;
  const styles = useMemo(() => buildStyles(colors), [theme]);

  /** Scroll the message list to the bottom. */
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  /** Load agent configuration on mount. */
  useEffect(() => {
    let cancelled = false;

    async function loadConfig() {
      try {
        const config = await MobileChatService.getConfig();
        if (cancelled) return;
        setAgentConfig(config);

        const initialMessages = config.agentGreeting
          ? [{ role: 'assistant', content: config.agentGreeting }]
          : [];
        setMessages(initialMessages);
      } catch (err) {
        if (!cancelled) {
          setConfigError(err.message);
        }
      }
    }

    loadConfig();
    return () => { cancelled = true; };
  }, []);

  const sendMessageText = useCallback(async (messageText, options = {}) => {
    const { baseMessages = messages, clearInput = false } = options;
    const trimmed = (messageText || '').trim();
    if (!trimmed || isLoading) return;

    const userMessage = { role: 'user', content: trimmed };
    const updatedMessages = [...baseMessages, userMessage];

    setMessages(updatedMessages);
    if (clearInput) {
      setInputMessage('');
    }
    setIsLoading(true);

    try {
      const response = await MobileChatService.sendMessage({
        messages: updatedMessages.map(({ role, content }) => ({ role, content })),
        conversationId,
        previousResponseId,
      });

      const assistantContent =
        response.message || response.content || response.reply || '';
      const assistantMessage = { role: 'assistant', content: assistantContent };

      setMessages((prev) => [...prev, assistantMessage]);

      if (response.conversationId) {
        setConversationId(response.conversationId);
      }
      if (response.previousResponseId !== undefined) {
        setPreviousResponseId(response.previousResponseId);
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: 'error', content: err.message },
      ]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  }, [messages, isLoading, conversationId, previousResponseId]);

  useEffect(() => {
    if (!agentConfig || hasInjectedInitialMessageRef.current) return;

    const initialMessage = initialMessageRef.current;
    if (!initialMessage) return;

    hasInjectedInitialMessageRef.current = true;
    const initialMessages = agentConfig.agentGreeting
      ? [{ role: 'assistant', content: agentConfig.agentGreeting }]
      : [];
    void sendMessageText(initialMessage, { baseMessages: initialMessages });
  }, [agentConfig, sendMessageText]);

  /** Auto-scroll when messages change. */
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  /** Send the current input message. */
  const handleSend = useCallback(async () => {
    await sendMessageText(inputMessage, { clearInput: true });
  }, [inputMessage, sendMessageText]);

  /** Submit on Enter (but not Shift+Enter). */
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  /** Render a single message bubble. */
  const renderMessage = useCallback((msg, index) => {
    if (msg.role === 'user') {
      return (
        <div key={index} style={styles.userMessage}>
          {msg.content}
        </div>
      );
    }

    if (msg.role === 'error') {
      return (
        <div key={index} style={styles.errorMessage}>
          {msg.content}
        </div>
      );
    }

    return (
      <div key={index} style={styles.assistantMessage}>
        <EnhancedMessageRenderer content={msg.content} role="assistant" artifacts={msg.artifacts} />
      </div>
    );
  }, [styles]);

  if (configError) {
    return (
      <div style={styles.container}>
        <div style={styles.errorBanner}>
          Unable to load chat configuration: {configError}
        </div>
      </div>
    );
  }

  if (!agentConfig) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.loadingDots}>
            <span style={styles.dot} />
            <span style={{ ...styles.dot, animationDelay: '0.2s' }} />
            <span style={{ ...styles.dot, animationDelay: '0.4s' }} />
          </div>
          <p style={styles.loadingText}>Loading...</p>
        </div>
      </div>
    );
  }

  const isSendDisabled = !inputMessage.trim() || isLoading;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={{ flex: 1 }}>
          <h1 style={styles.headerTitle}>{agentConfig.agentName || 'Chat'}</h1>
          {agentConfig.agentDescription && (
            <p style={styles.headerSubtitle}>{agentConfig.agentDescription}</p>
          )}
        </div>
        <button
          style={styles.themeToggle}
          onClick={toggleTheme}
          aria-label={`Switch to ${theme === THEMES.DARK ? 'light' : 'dark'} mode`}
        >
          {theme === THEMES.DARK ? '\u2600\uFE0F' : '\uD83C\uDF19'}
        </button>
      </div>

      {/* Messages */}
      <div style={styles.messagesContainer}>
        {messages.map(renderMessage)}

        {isLoading && (
          <div style={styles.assistantMessage}>
            <div style={styles.loadingDots}>
              <span style={styles.dot} />
              <span style={{ ...styles.dot, animationDelay: '0.2s' }} />
              <span style={{ ...styles.dot, animationDelay: '0.4s' }} />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div style={styles.inputContainer}>
        <textarea
          ref={inputRef}
          style={styles.input}
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          rows={1}
          disabled={isLoading}
        />
        <button
          style={{
            ...styles.sendButton,
            ...(isSendDisabled ? styles.sendButtonDisabled : {}),
          }}
          onClick={handleSend}
          disabled={isSendDisabled}
          aria-label="Send message"
        >
          Send
        </button>
      </div>

      {/* Keyframe animation for loading dots */}
      <style>{`
        @keyframes mobileChatBounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

/**
 * Builds the inline styles object from the active color palette.
 * @param {typeof darkColors} c - Active color palette.
 * @returns {Record<string, React.CSSProperties>}
 */
function buildStyles(c) {
  return {
    container: {
      position: 'fixed',
      inset: 0,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: c.containerBg,
      color: c.textPrimary,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    header: {
      padding: '12px 16px',
      borderBottom: `1px solid ${c.headerBorder}`,
      backgroundColor: c.headerBg,
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      flexShrink: 0,
    },
    headerTitle: {
      fontSize: '16px',
      fontWeight: 600,
      margin: 0,
    },
    headerSubtitle: {
      fontSize: '12px',
      color: c.textSecondary,
      margin: 0,
    },
    themeToggle: {
      padding: '6px 10px',
      borderRadius: '8px',
      border: 'none',
      backgroundColor: c.toggleBg,
      color: c.toggleText,
      fontSize: '16px',
      cursor: 'pointer',
      flexShrink: 0,
      lineHeight: 1,
    },
    messagesContainer: {
      flex: 1,
      overflowY: 'auto',
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
    userMessage: {
      alignSelf: 'flex-end',
      backgroundColor: c.userMessageBg,
      color: c.userMessageText,
      padding: '10px 14px',
      borderRadius: '16px 16px 4px 16px',
      maxWidth: '80%',
      wordBreak: 'break-word',
      fontSize: '14px',
      lineHeight: '1.5',
    },
    assistantMessage: {
      alignSelf: 'flex-start',
      backgroundColor: c.assistantMessageBg,
      color: c.assistantMessageText,
      padding: '10px 14px',
      borderRadius: '16px 16px 16px 4px',
      maxWidth: '85%',
      wordBreak: 'break-word',
      fontSize: '14px',
      lineHeight: '1.5',
    },
    errorMessage: {
      alignSelf: 'center',
      backgroundColor: c.errorMessageBg,
      color: c.errorMessageText,
      padding: '8px 14px',
      borderRadius: '12px',
      maxWidth: '90%',
      fontSize: '13px',
      textAlign: 'center',
    },
    inputContainer: {
      padding: '12px 16px',
      borderTop: `1px solid ${c.headerBorder}`,
      backgroundColor: c.headerBg,
      display: 'flex',
      gap: '8px',
      alignItems: 'flex-end',
      flexShrink: 0,
    },
    input: {
      flex: 1,
      padding: '10px 14px',
      borderRadius: '20px',
      border: `1px solid ${c.inputBorder}`,
      backgroundColor: c.inputBg,
      color: c.inputText,
      fontSize: '14px',
      lineHeight: '1.5',
      outline: 'none',
      resize: 'none',
      maxHeight: '120px',
      fontFamily: 'inherit',
    },
    sendButton: {
      padding: '10px 16px',
      borderRadius: '20px',
      border: 'none',
      backgroundColor: c.sendButtonBg,
      color: c.sendButtonText,
      fontSize: '14px',
      fontWeight: 600,
      cursor: 'pointer',
      flexShrink: 0,
      opacity: 1,
    },
    sendButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    loadingContainer: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '12px',
    },
    loadingText: {
      fontSize: '14px',
      color: c.textSecondary,
      margin: 0,
    },
    loadingDots: {
      display: 'flex',
      gap: '4px',
      padding: '8px 0',
    },
    dot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor: c.dotBg,
      display: 'inline-block',
      animation: 'mobileChatBounce 1.4s infinite ease-in-out both',
    },
    errorBanner: {
      padding: '12px 16px',
      backgroundColor: c.errorMessageBg,
      color: c.errorMessageText,
      textAlign: 'center',
      fontSize: '14px',
    },
  };
}

export default MobileChatPage;
